import React from "react";
import { Link } from "react-router-dom";
import Icon from "./ui/Icon";

const features = [
  {
    icon: "grid",
    number: "01",
    title: "An agent for every idea.",
    description:
      "Give your agent a purpose, connect its tools, and make it your own.",
    link: "/agent-hub",
    action: "Explore Agent Hub",
    tone: "green",
  },
  {
    icon: "activity",
    number: "02",
    title: "Give your ideas a flow.",
    description:
      "Connect agents on a canvas, set a trigger, and build a workflow around your work.",
    link: "/workflows",
    action: "Build a workflow",
    tone: "sand",
  },
  {
    icon: "code",
    number: "03",
    title: "A useful app, made yours.",
    description:
      "Describe what you need and create an app with a dashboard built around it.",
    link: "/apps",
    action: "Explore your apps",
    tone: "blue",
  },
];

function WorkflowPreview() {
  return (
    <div
      className="home-preview"
      aria-label="Illustration of an example research agent workflow"
    >
      <div className="preview-topline">
        <span>
          <span className="preview-dot" /> YOUR NEXT WORKFLOW
        </span>
        <span className="preview-example">Example</span>
      </div>
      <div className="preview-canvas">
        <div className="preview-note">
          <Icon name="spark" size={15} /> A little direction. A lot of
          possibility.
        </div>
        <div className="workflow-node workflow-trigger">
          <span className="workflow-icon">
            <Icon name="clock" />
          </span>
          <div>
            <small>START WITH A PLAN</small>
            <strong>Every Monday, 9:00 AM</strong>
          </div>
          <Icon name="more" size={18} />
        </div>
        <div className="workflow-connector">
          <span />
        </div>
        <div className="workflow-agent">
          <div className="workflow-agent-heading">
            <span className="workflow-icon workflow-icon--solid">
              <Icon name="spark" size={25} />
            </span>
            <div>
              <strong>Research companion</strong>
              <span>Your custom AI agent</span>
            </div>
            <span className="workflow-agent-badge">AGENT</span>
          </div>
          <p>“Find what matters. Bring me the big picture.”</p>
          <div className="workflow-tools">
            <span>
              <Icon name="globe" size={14} /> Web search
            </span>
            <span>
              <Icon name="document" size={14} /> Summarize
            </span>
          </div>
        </div>
        <div className="workflow-connector">
          <span />
        </div>
        <div className="workflow-node workflow-result">
          <span className="workflow-icon">
            <Icon name="document" />
          </span>
          <div>
            <small>MAKE SOMETHING USEFUL</small>
            <strong>A brief, ready when you are</strong>
          </div>
          <Icon name="check" size={18} />
        </div>
      </div>
      <div className="preview-bottomline">
        <span>
          <Icon name="grid" size={14} /> Your tools. Your workflow.
        </span>
        <span>
          Built around you <Icon name="arrowUpRight" size={14} />
        </span>
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <div className="home-page">
      <section className="home-hero" aria-labelledby="home-heading">
        <div className="home-hero-copy">
          <span className="ui-eyebrow">
            <span className="ui-status-dot" /> A WORKSPACE FOR WHAT’S NEXT
          </span>
          <h1 id="home-heading">
            Big ideas.
            <br />A little <em>backup.</em>
          </h1>
          <p>
            Meet your space to think, create, and get things done with AI. Bring
            your ideas. Build your agents. Make room for what matters.
          </p>
          <div className="home-hero-actions">
            <Link to="/agent-hub" className="ui-button ui-button--primary">
              Open Agent Hub <Icon name="arrow" size={18} />
            </Link>
            <Link to="/workflows" className="ui-button ui-button--text">
              Build a workflow <Icon name="arrowUpRight" size={17} />
            </Link>
          </div>
          <div className="home-hero-footnote">
            <span>
              <Icon name="check" size={15} /> Start with a template
            </span>
            <span>
              <Icon name="check" size={15} /> Choose your AI provider
            </span>
          </div>
        </div>
        <WorkflowPreview />
      </section>
      <section
        className="home-capabilities"
        aria-labelledby="capabilities-heading"
      >
        <div className="home-section-heading">
          <div>
            <span className="ui-eyebrow">LESS FRICTION. MORE FLOW.</span>
            <h2 id="capabilities-heading">A few ways to make it yours.</h2>
          </div>
          <Link className="ui-text-link" to="/about">
            Find your starting point <Icon name="arrow" size={17} />
          </Link>
        </div>
        <div className="home-feature-grid">
          {features.map((feature) => (
            <Link
              to={feature.link}
              className="home-feature"
              key={feature.number}
            >
              <div className="home-feature-top">
                <span
                  className={`home-feature-icon home-feature-icon--${feature.tone}`}
                >
                  <Icon name={feature.icon} size={23} />
                </span>
                <span>{feature.number}</span>
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
              <span className="home-feature-link">
                {feature.action}
                <Icon name="arrowUpRight" size={18} />
              </span>
            </Link>
          ))}
        </div>
      </section>
      <section className="home-bottom-banner">
        <div className="home-banner-icon">
          <Icon name="plus" size={26} />
        </div>
        <div>
          <h2>Your workflow doesn’t have to look like everyone else’s.</h2>
          <p>Start small. Make an agent that does one thing well.</p>
        </div>
        <Link className="ui-button ui-button--secondary" to="/agent-hub/create">
          Create your first agent <Icon name="arrow" size={17} />
        </Link>
      </section>
    </div>
  );
}
